import {
  ExternalMcpFormValues,
  PromptExternalMcpItem,
} from 'lauria/types/externalMcp'
import { SelectedMcpApplication } from 'lauria/types/mcpApplication'
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

type UsePromptFormAdvancedSectionParams = {
  setSelectedAgents: Dispatch<SetStateAction<SelectedMcpApplication[]>>
  onAddExternalMcp: (values: ExternalMcpFormValues) => void
  onUpdateExternalMcp: (
    localId: string,
    values: Pick<ExternalMcpFormValues, 'authMethod' | 'url'>
  ) => void
  onDeleteExternalMcp: (localId: string) => void
}

export const usePromptFormAdvancedSection = ({
  setSelectedAgents,
  onAddExternalMcp,
  onUpdateExternalMcp,
  onDeleteExternalMcp,
}: UsePromptFormAdvancedSectionParams) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingItem, setEditingItem] = useState<PromptExternalMcpItem | null>(
    null
  )
  const [deletingItem, setDeletingItem] =
    useState<PromptExternalMcpItem | null>(null)

  const handleAgentRemove = useCallback(
    (agentId: number) => {
      setSelectedAgents((previousAgents) =>
        previousAgents.filter((agent) => agent.id !== agentId)
      )
    },
    [setSelectedAgents]
  )

  const openCreateModal = useCallback(() => {
    setFormMode('create')
    setEditingItem(null)
    setIsFormModalOpen(true)
  }, [])

  const openEditModal = useCallback((item: PromptExternalMcpItem) => {
    setFormMode('edit')
    setEditingItem(item)
    setIsFormModalOpen(true)
  }, [])

  const openDeleteModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: PromptExternalMcpItem) => {
      event.stopPropagation()
      setDeletingItem(item)
      setIsDeleteModalOpen(true)
    },
    []
  )

  const closeFormModal = useCallback(() => {
    setIsFormModalOpen(false)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const handleFormSubmit = useCallback(
    (values: ExternalMcpFormValues) => {
      if (formMode === 'create') {
        onAddExternalMcp(values)
        return
      }

      if (editingItem) {
        onUpdateExternalMcp(editingItem.localId, values)
      }
    },
    [editingItem, formMode, onAddExternalMcp, onUpdateExternalMcp]
  )

  const handleDeleteConfirm = useCallback(() => {
    if (deletingItem) {
      onDeleteExternalMcp(deletingItem.localId)
    }
  }, [deletingItem, onDeleteExternalMcp])

  const formInitialValues = useMemo(
    () =>
      editingItem
        ? {
            authMethod: editingItem.authMethod,
            url: editingItem.url,
          }
        : undefined,
    [editingItem]
  )

  return {
    handleAgentRemove,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    formModal: {
      open: isFormModalOpen,
      mode: formMode,
      initialValues: formInitialValues,
      onClose: closeFormModal,
      onSubmit: handleFormSubmit,
    },
    deleteModal: {
      open: isDeleteModalOpen,
      onClose: closeDeleteModal,
      onConfirm: handleDeleteConfirm,
    },
  }
}
